import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useMemo, useCallback } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { ServerTable } from 'shared/ui/ServerTable';
import { ServerTableRowSelectionActions } from 'shared/ui/ServerTable/types';
import { useGetPaginatedUsersQuery, User } from 'entities/User';
import { createZoneModalActions } from '../../model/slice/createZoneModalSlice';
import { getZoneBasics, getZoneSelectedBoxIds, getZoneSelectedDoorIds, getZoneSelectedUserIds } from '../../model/selectors';
import { useCreateZoneMutation, ZoneObjectType } from 'entities/Zone';
import { showNotification } from 'app/providers/NotificationsProvider';

interface Props {
  onClose: () => void;
}

export const SelectUsersStep: FC<Props> = memo(({ onClose }) => {
  const { t } = useAppTranslation(['common']);

  const dispatch = useAppDispatch();
  const basics = useAppSelector(getZoneBasics);
  const boxIds = useAppSelector(getZoneSelectedBoxIds);
  const doorIds: string[] = useAppSelector(getZoneSelectedDoorIds);

  const [createZone, { isLoading }] = useCreateZoneMutation();

  const columns = useMemo<ColumnsType<User>>(
    () => [
      { title: t('Name'), key: 'name', render: (_, record) => `${record.firstName} ${record.lastName}` },
      { title: t('Email'), key: 'email', render: (_, record) => record.email },
      { title: t('Phone'), key: 'phone', render: (_, record) => record.phone },
    ],
    [t],
  );

  const finish = useCallback(
    async (selectedUsers: User[]) => {
      const userIds: string[] = selectedUsers.map((user: User) => user.userId);

      dispatch(createZoneModalActions.setSelectedUserIds(userIds));

      const payload = {
        name: basics?.name || '',
        warehouseId: basics?.warehouse?.warehouseId || '',
        users: userIds.map((id: string) => ({ userId: id })),
        objects: [
          ...boxIds.map((id) => ({ objectType: ZoneObjectType.BOX, objectId: id })),
          ...doorIds.map((id) => ({ objectType: ZoneObjectType.DOOR, objectId: id })),
        ],
      };

      try {
        await createZone(payload).unwrap();

        showNotification('info', t('Success', { ns: 'common' }), t('Zone has been successfully created', { ns: 'common' }));
        onClose();
      } catch {
        showNotification('error', t('Error', { ns: 'common' }), t('Something went wrong', { ns: 'common' }));
      }
    },
    [basics, boxIds, doorIds, createZone, dispatch, onClose, t],
  );

  const rowSelectionActions: Array<ServerTableRowSelectionActions<User>> = [
    {
      name: isLoading ? t('Saving...', { ns: 'common' }) : t('Create', { ns: 'common' }),
      onClick: finish,
      isDisabled: isLoading,
    },
  ];

  return (
    <>
      <ServerTable
        columns={columns}
        fetchData={useGetPaginatedUsersQuery}
        rowKey="userId"
        search
        title={t('Choose users', { ns: 'warehouses' })}
        selectionType="checkbox"
        rowSelectionActions={rowSelectionActions}
        isEmptySelectionAllowed={true}
      />
    </>
  );
});
